/* External */
import {
  memo, useCallback, useEffect, useMemo, useState, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useLocation, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

/* Ott-common */
import { MyListAssetType } from '@dtvgo/backend-service/normalize';

/* Providers */
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';

/* Components */
import DetailContainer from '../../../../components/DetailContainer';
import AddRemoveWatchListButton from '../../../../components/AddRemoveWatchListButton';
import MoreLikeThisVodEpg from '../../../../components/MoreLikeThisVodEpg/MoreLikeThisVodEpg';
import ScrollView from '../../../../components/ScrollView/ScrollView';
import Tabs from '../../../../components/Tabs/Tabs';
import PlayResumeButton from '../../../../components/PlayResumeButton/PlayResumeButton';
import WatchFromStartButton from '../../../../components/WatchFromStartButton';
import DetailsSelector from '../../../../components/DetailsSelector';
import FocusableButton from '../../../../components/FocusableButton';

/* Utils */
import useFetchContent from '../../../../hooks/useFetchContent';
import useFetchVodContentProgress from '../../../../hooks/useFetchVodContentProgress';
import useLockIcon from '../../../../hooks/useLockIcon';
import parentalControlBlock from '../../../../utils/parentalControlUtils';
import useRedirectToPlayer from '../../../../hooks/useRedirectToPlayer';
import { Direction } from '../../../../utils/spatialNavigation';
import { PLAY_SOURCE_PAGE } from '../../../../utils/trackSourceConviva';
import {
  DOT_SEPARATOR,
  REPORT_GA,
  RoutesOriginCarousels,
  STARTED_PAGE_ID,
} from '../../../../utils/constants';
import { isSpideoMoreLikeThisActive, timesPressedBackButtonOnPdp } from '../../../../utils/env';
import {
  getAllGenres,
  getFormattedDuration,
  useFullEpisodeName,
  getRating,
  useTitle,
  isEpisode,
  joinArrayWithSeparator,
  joinArrayWithSpace,
  isSelfClassified,
  isSports,
} from '../../../../utils/metaDataUtils';
import { gaService } from '../../../../services/gaService';
import {
  formatContentData,
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
  playerType,
  transmissionContentType,
} from '../../../../utils/googleTags';
import { redirectTo } from '../../../../utils/history';
import { getLink, Routes } from '../../../../utils/router';
import { useOnBackPress } from '../../../../utils/keyDownEvents';

/*  Storages */
import {
  increasePdpHistory,
  decreasePdpHistory,
  selectPdpHistory,
  increaseBackButtonHistory,
  selectBackButtonHistory,
} from '../../../../storage/pdpHistory';

/* Style */
import './Vod.scss';

const requiredProps = ['title', 'description', 'genres', 'rating', 'ratingDescription', 'ratingWarning', 'images', 'releaseYear', 'duration'];

function Vod({ realFocusKey, setFocus, sourceProvider }) {
  const BUTTON_CONTAINER_FOCUS_KEY = `${realFocusKey}-buttons-container`;
  const PLAY_RESUME_FOCUS_KEY = `${realFocusKey}-play-resume`;
  const BACK_HOME_BUTTON_KEY = `${realFocusKey}-back-home-button`;

  const { contentId } = useParams();

  const history = useHistory();
  const { state: locationState } = useLocation();
  const {
    commonTaggingEvent,
    gtmChangeItem,
    gtmState: { pdpSelectedTabTitle },
  } = useGtmLocalContext();
  const tagsMountRef = useRef(false);

  const [showTabs, setShowTabs] = useState(true);
  const { content } = useFetchContent({
    contentId,
    sourceProvider,
    requiredProps,
  });
  const {
    title: contentTitle,
    description,
    duration,
    vmsId,
    moreLikeThis: moreLikeThisItems,
    vrioAssetId,
  } = content || {};
  const {
    savedProgress,
  } = useFetchVodContentProgress({
    contentId,
    duration,
  });

  const progressOffset = useMemo(() => {
    if (savedProgress >= duration * 0.95) {
      return 0;
    }

    return savedProgress;
  }, [duration, savedProgress]);

  const fullEpisodeName = useFullEpisodeName(content, DOT_SEPARATOR);
  const metadata = useMemo(() => {
    if (content) {
      return joinArrayWithSeparator([
        isEpisode(content) && fullEpisodeName,
        content?.releaseYear,
        getFormattedDuration(content),
        getAllGenres(content, DOT_SEPARATOR),
      ], DOT_SEPARATOR);
    }
    return '';
  }, [content, fullEpisodeName]);

  const title = useTitle(content);

  const subtitle = useMemo(() => joinArrayWithSpace([
    getRating(content),
    metadata,
  ]), [content, metadata]);

  const [isScrolled, setIsScrolled] = useState(false);

  const dispatch = useDispatch();
  const pdpHistory = useSelector(selectPdpHistory);
  const backButtonHistory = useSelector(selectBackButtonHistory);
  const shouldDisplayBackHomeButton = isSpideoMoreLikeThisActive && pdpHistory.length > 1;

  /* eslint-disable-next-line react-hooks/exhaustive-deps
  -- phdHistory causes an unwanted side effect */
  const shouldAddIdToPdpHistory = useMemo(() => !pdpHistory.includes(contentId), [contentId]);

  useEffect(() => {
    setFocus(PLAY_RESUME_FOCUS_KEY);
  }, [PLAY_RESUME_FOCUS_KEY, setFocus]);
  useEffect(() => {
    setIsScrolled(false);
    setFocus(PLAY_RESUME_FOCUS_KEY);
  }, [PLAY_RESUME_FOCUS_KEY, setFocus, contentId]);

  useEffect(() => {
    if (moreLikeThisItems && moreLikeThisItems.size === 0) {
      setShowTabs(false);
    }
  }, [moreLikeThisItems]);

  const handleWheel = useCallback((event) => {
    setIsScrolled(showTabs && event.deltaY > 0);
  }, [showTabs]);

  const handleArrowPressTabs = useCallback((direction) => {
    if (direction === Direction.UP) {
      setIsScrolled(false);
      setFocus(BUTTON_CONTAINER_FOCUS_KEY);
      return false;
    }

    return true;
  }, [BUTTON_CONTAINER_FOCUS_KEY, setFocus]);

  const isNonEntitledContent = useLockIcon(content);
  const isContentBlockedByParentalControl = parentalControlBlock(content);

  const handleRedirectToPlayer = useRedirectToPlayer(content, {
    page: PLAY_SOURCE_PAGE.PDP,
    section: contentTitle || '',
    state: locationState,
  });

  const handleWatchFromStart = useRedirectToPlayer(content, {
    page: PLAY_SOURCE_PAGE.PDP,
    section: contentTitle || '',
    query: { watchFromStart: true },
    state: locationState,
  });

  const ratingDescription = isSelfClassified(content) ? '' : content?.ratingDescription;
  const ratingWarning = isSelfClassified(content) ? '' : content?.ratingWarning;

  useEffect(() => {
    gaService.sendEvent({
      action: REPORT_GA.GA_EVENT_ACTION_PDP_VIEW_PDP_PAGE,
      label: REPORT_GA.GA_EVENT_LABEL_PDP_VIEW_PDP_PAGE,
      category: REPORT_GA.GA_EVENT_CATEGORY_PDP,
    });
  }, []);

  const pdpCommonTagEvent = useCallback(({ eventName, subcategory }) => {
    if (eventName === GTM_EVENT_NAMES.PDP.WATCH_CONTENT) {
      tagsMountRef.current = false;
    }
    const {
      collectionName,
      componentType,
      gtmPlaySource,
      providerName,
      sectionName,
      transmissionType,
    } = locationState;

    commonTaggingEvent({
      category: GTM_CATEGORIES.PDP_MOVIE_SERIE_LIVE_DATA,
      subCategory: subcategory || GTM_SUBCATEGORIES.PDP.MOVIES_WATCH,
      eventName,
      data: {
        content: {
          ...formatContentData({
            content,
            rest: {
              progress: savedProgress,
              playSource: gtmPlaySource,
            },
          }),
        },
        componentType,
        transmissionType: transmissionContentType(transmissionType),
        sectionName,
        collectionName,
        providerName,
        isSports: isSports(content),
        playerType: playerType(content?.type, false, isSports(content)),
        filterPdp: pdpSelectedTabTitle,
      },
    });
  }, [commonTaggingEvent, content, locationState, pdpSelectedTabTitle, savedProgress]);

  useEffect(() => {
    if (isSpideoMoreLikeThisActive && shouldAddIdToPdpHistory) {
      dispatch(increasePdpHistory(contentId));
    }
  }, [contentId, dispatch, shouldAddIdToPdpHistory]);

  useEffect(() => {
    if (content && !tagsMountRef.current) {
      pdpCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = true;
    }
  }, [content, pdpCommonTagEvent]);

  const handleEnterPress = useCallback(() => {
    handleRedirectToPlayer();
    pdpCommonTagEvent({
      eventName: savedProgress ? GTM_EVENT_NAMES.PDP.RESUME : GTM_EVENT_NAMES.PDP.WATCH,
    });
  }, [handleRedirectToPlayer, pdpCommonTagEvent, savedProgress]);

  const handleBackHome = useCallback(() => {
    redirectTo(getLink(Routes.Private.Home));
  }, []);

  const handleEnterPressWatchFromStart = useCallback(() => {
    handleWatchFromStart();
    pdpCommonTagEvent({ eventName: GTM_EVENT_NAMES.PDP.RESTART });
  }, [handleWatchFromStart, pdpCommonTagEvent]);

  const idTextPlayResumeButton = useMemo(() => {
    if (isNonEntitledContent || isContentBlockedByParentalControl) {
      return 'common.PlayButtonLockIcon';
    }
    return progressOffset > 0 ? 'common.resume' : 'common.watchNow';
  }, [progressOffset, isNonEntitledContent, isContentBlockedByParentalControl]);

  const pageId = sessionStorage.getItem(STARTED_PAGE_ID);

  const handleOnBack = useCallback(() => {
    if (backButtonHistory === timesPressedBackButtonOnPdp) {
      redirectTo(getLink(RoutesOriginCarousels[pageId]));
      return;
    }

    dispatch(increaseBackButtonHistory());
    dispatch(decreasePdpHistory(contentId));

    history.goBack();
  }, [backButtonHistory, dispatch, pageId, history, contentId]);

  useOnBackPress({
    listener: handleOnBack,
    when: isSpideoMoreLikeThisActive,
  });

  useEffect(() => {
    if (isScrolled && pdpSelectedTabTitle) {
      pdpCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PDP.FILTER_TAB_PDP,
        subcategory: GTM_SUBCATEGORIES.PDP.MOVIES_RELATED_CONTENT,
      });
    }
  }, [isScrolled, pdpCommonTagEvent, pdpSelectedTabTitle]);

  useEffect(() => {
    if (!isScrolled && pdpSelectedTabTitle) {
      gtmChangeItem({ pdpSelectedTabTitle: undefined });
    }
  }, [isScrolled, gtmChangeItem, pdpSelectedTabTitle]);

  return (
    <div
      className={`dtv-html5-c-detail-vod ${isScrolled ? 'dtv-html5-c-detail-vod--scrolled' : ''}`}
      onWheel={handleWheel}
    >
      <DetailContainer content={content} isScrolled={isScrolled}>
        {shouldDisplayBackHomeButton && (
          <div className="dtv-html5-c-detail-vod__content-back-home-button">
            <FocusableButton
              className="dtv-html5-c-detail-vod__back-home-button"
              onEnterPress={handleBackHome}
              focusKey={BACK_HOME_BUTTON_KEY}
              icon="arrow-back"
              primary
            >
              <FormattedMessage id="pdp.backButtonTitle" />
            </FocusableButton>
          </div>
        )}

        <div className="dtv-html5-c-detail-vod__content-title">
          <DetailContainer.Title className="dtv-html5-c-detail-vod__title">
            {title || ''}
          </DetailContainer.Title>
        </div>

        <div className="dtv-html5-c-detail-vod__subtitle">
          {subtitle}
        </div>

        <DetailContainer.Description className="dtv-html5-c-detail-vod__description">
          {description || ''}
        </DetailContainer.Description>

        <DetailContainer.Buttons
          focusKey={BUTTON_CONTAINER_FOCUS_KEY}
          className="dtv-html5-c-detail-vod__buttons"
        >
          <PlayResumeButton
            focusKey={PLAY_RESUME_FOCUS_KEY}
            isNonEntitledContent={isNonEntitledContent}
            isContentBlockedByParentalControl={isContentBlockedByParentalControl}
            progress={progressOffset}
            duration={duration}
            onPress={handleEnterPress}
            blockUpArrow={!shouldDisplayBackHomeButton}
          >
            <FormattedMessage id={idTextPlayResumeButton} />
          </PlayResumeButton>
          {
            progressOffset > 0
              ? <WatchFromStartButton onPress={handleEnterPressWatchFromStart} />
              : undefined
          }
          {!isEpisode(content) && (
            <AddRemoveWatchListButton
              vmsId={vmsId}
              contentId={contentId}
              vrioAssetId={vrioAssetId}
              type={MyListAssetType.MOVIE}
              content={content}
              locationState={locationState}
            />
          )}
        </DetailContainer.Buttons>
      </DetailContainer>

      {showTabs && (
        <Tabs
          className={showTabs ? '' : 'dtv-html5-c-detail-vod__more-like-this--hidden'}
          onScrolledChange={setIsScrolled}
          onArrowPress={handleArrowPressTabs}
          disableFocus={!showTabs}
          showActiveTab={isScrolled}
          loadDefaultTab
        >
          {isSpideoMoreLikeThisActive && (
            <div className="dtv-html5-c-detail-serie__content-episodes" data-labeltab="tabs.title.related.content">
              <ScrollView
                resetScrollPosition={!isScrolled}
                propagateEventOnReachTop
              >
                <MoreLikeThisVodEpg
                  className="dtv-html5-c-detail-vod__more-like-this"
                  contentId={contentId}
                  pdpCommonTagEvent={pdpCommonTagEvent}
                  isDisplaying={isScrolled}
                />
              </ScrollView>
            </div>
          )}
          <div className="dtv-html5-c-detail-serie__content-episodes" data-labeltab="common.details">
            <div className="dtv-html5-c-detail-serie__seasons">
              <DetailsSelector
                title={title}
                description={content?.description}
                genres={content?.genres?.join(' • ')}
                ageRating={getRating(content)}
                ratingDescription={ratingDescription}
                ratingWarning={ratingWarning}
                type={MyListAssetType.MOVIE}
              />
            </div>
          </div>
        </Tabs>
      )}
    </div>
  );
}

Vod.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
  sourceProvider: PropTypes.string,
};

export default memo(withFocusable()(memo(Vod)));
